import React from 'react';
import { type Key, type SWRConfiguration, unstable_serialize, useSWRConfig } from 'swr';

const DEFAULT_STALE_CONFIG: SWRConfiguration = {
  refreshInterval: 0,
  refreshWhenHidden: false,
  refreshWhenOffline: false,
  revalidateIfStale: false,
  revalidateOnReconnect: false,
  revalidateOnFocus: false,
};

/**
 * Returns SWR config that keeps data stale (no background revalidation). Optionally controls
 * whether to revalidate on mount when the cache entry has an error.
 *
 * @experimental
 * To be used in the future. Refer to this epic for more details: https://solink.atlassian.net/browse/SL-3140
 *
 * @param key - The key to use for the cache
 * @param revalidateOnMountWhenCachedError - When `true` (default), revalidate on mount even when
 *   the cache has an error (retry on mount). When `false`, do not revalidate on mount when the
 *   cache has an error—useful to avoid retries for known-failing or permission-gated requests.
 * @returns SWR configuration with revalidateOnMount derived from cache state and the option above
 */
export default function useStaleSWRConfig(
  key: Key,
  revalidateOnMountWhenCachedError: boolean = true,
) {
  const { cache } = useSWRConfig();

  const serializedKey = unstable_serialize(key);

  const cachedInfo = React.useMemo(() => {
    return cache?.get(serializedKey);
  }, [serializedKey, cache]);

  return {
    ...DEFAULT_STALE_CONFIG,
    revalidateOnMount: revalidateOnMountWhenCachedError
      ? !cachedInfo?.data
      : !cachedInfo?.data && !cachedInfo?.error,
  };
}
